/**
 * Sell-draft contract — versioned payload for `listing_drafts.payload` plus
 * the wire bodies of `POST /api/listings/drafts/save` and
 * `POST /api/listings/drafts/delete`.
 *
 * A draft is an in-progress listing-creation form saved EXPLICITLY by the
 * seller (quit-dialog "Save as draft"), persisted server-side so it can be
 * resumed on another device. Multiple drafts per user, capped at
 * `DRAFTS_PER_USER_CAP` (lib/listings/sell-draft.ts).
 *
 * Shape philosophy (mirrors `listing-create.ts`'s `cardDraft` precedent):
 *  - `photos` is STRICT — the image-file lifecycle (save-time upload, prune
 *    on re-save, cleanup on discard, reuse on publish) depends on the server
 *    structurally understanding this part, and `rawImageRefField()` makes the
 *    boundary parse also the `RawImageRef` mint.
 *  - `form` is a permissive record — a strict mirror of the web `ListingData`
 *    would drift on every form change (and `types/listing.ts` is not
 *    importable from this package). The web domain layer
 *    (`lib/listings/sell-draft.ts::parseListingDraft`) owns rehydration.
 *  - `version` is a literal — an unknown version parses as failure and the
 *    draft is surfaced as "unreadable" (delete-only), never silently dropped.
 *
 * @see lib/listings/sell-draft.ts — serialize/parse/meaningful (pure domain)
 * @see supabase/migrations/*_listing_drafts.sql — table + RLS
 * @see app/api/listings/drafts/{save,delete}/route.ts — consumers
 */

import { z } from "zod";

import { pinned } from "./pin";
import { rawImageRefField, type RawImageRef } from "./raw-image-ref";
import { LISTING_CATEGORIES, type ListingCategory } from "./listing-category";

export const SELL_DRAFT_PAYLOAD_VERSION = 1;

/** One saved photo: bucket-relative path + the form's stable photo identity. */
export interface SellDraftPhoto {
  path: RawImageRef;
  photoId: string;
}

export interface SellDraftPayload {
  version: 1;
  /** ISO timestamp from the CLIENT clock — display only, never ordering. */
  savedAt: string;
  /** Wizard step index the seller was on — resume position (clamped on read). */
  step: number;
  listingCategory?: ListingCategory;
  photos: SellDraftPhoto[];
  /**
   * `ListingData` minus non-serializable/device-local state (`photos[].file`,
   * `editedPhotos`, `photoEditorState`, `id`, `createdAt`, `updatedAt`).
   * Deliberately permissive — see header.
   */
  form: Record<string, unknown>;
}

export const SellDraftPayloadSchema = pinned<SellDraftPayload>()(
  z.object({
    version: z.literal(1),
    savedAt: z.string(),
    step: z.number().int().min(0),
    listingCategory: z.enum(LISTING_CATEGORIES).optional(),
    photos: z.array(
      z.object({
        path: rawImageRefField(),
        photoId: z.string(),
      }),
    ),
    form: z.record(z.string(), z.unknown()),
  }),
);

// ---------------------------------------------------------------------------
// POST /api/listings/drafts/save
// ---------------------------------------------------------------------------

export interface SaveListingDraftRequest {
  /** Absent = create a new draft; present = update (RLS-scoped to owner). */
  draftId?: string;
  payload: SellDraftPayload;
  /**
   * Denormalized copy of `payload.photos[].path` for the row's `image_paths`
   * column — file cleanup must never depend on parsing a (possibly
   * future-versioned) payload. The server re-derives the prune diff from its
   * own previous row; this list is never trusted as a delete set.
   */
  imagePaths: RawImageRef[];
  /** Denormalized for the drafts-list card (readable even if payload isn't). */
  title: string;
  listingCategory?: ListingCategory;
}

export const SaveListingDraftRequestSchema = pinned<SaveListingDraftRequest>()(
  z.object({
    draftId: z.string().uuid().optional(),
    payload: SellDraftPayloadSchema,
    imagePaths: z.array(rawImageRefField()),
    title: z.string(),
    listingCategory: z.enum(LISTING_CATEGORIES).optional(),
  }),
);

export interface SaveListingDraftResponse {
  draftId: string;
  updatedAt: string;
}

export const SaveListingDraftResponseSchema = pinned<SaveListingDraftResponse>()(
  z.object({
    draftId: z.string(),
    updatedAt: z.string(),
  }),
);

// ---------------------------------------------------------------------------
// POST /api/listings/drafts/delete
// ---------------------------------------------------------------------------

export interface DeleteListingDraftRequest {
  draftId: string;
}

export const DeleteListingDraftRequestSchema = pinned<DeleteListingDraftRequest>()(
  z.object({
    draftId: z.string().uuid(),
  }),
);
